import { io } from 'socket.io-client';

class SocketManager {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 1;
    this.reconnectDelay = 1000;
    this.currentToken = null;
    this.currentUserId = null;
    this.connectionRefs = 0;
  }

  connect(token, userId = null) {
    // Increment reference counter
    this.connectionRefs++;

    // Validate token before attempting connection
    if (!token || token.trim() === '') {
      console.error('❌ Cannot connect to socket: No valid token provided');
      this.connectionRefs--; // Decrement on error
      return;
    }

    // Check if already connected with same parameters
    if (this.socket && this.socket.connected && 
        this.currentToken === token && this.currentUserId === userId) {
      return;
    }

    // Check if already connecting with same parameters
    if (this.isConnecting && this.currentToken === token && this.currentUserId === userId) {
      return;
    }

    // If we have a different userId or token, we need to reconnect
    if (this.socket && (this.currentToken !== token || this.currentUserId !== userId)) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isConnecting = false;
    }

    // Set connecting flag to prevent multiple simultaneous connections
    this.isConnecting = true;

    // Use the socket server URL from environment or default
    const baseURL = process.env.REACT_APP_SOCKET_URL ;
    
    this.currentToken = token;
    this.currentUserId = userId;
    
    this.socket = io(baseURL, {
      auth: {
        token: this.currentToken,
        userType: 'SANDBOX',
        sandboxUserId: this.currentUserId || null
      },
      transports: ['websocket'],
      timeout: 20000,
      forceNew: false,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      upgrade: true,
      rememberUpgrade: false
    });
    

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('✅ Socket connected successfully!');
      this.isConnected = true;
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.emit('connected');
    });

    this.socket.on('disconnect', (reason) => {
      this.isConnected = false;
      this.emit('disconnected', reason);
      
      // Auto-reconnect on unexpected disconnection
      if (reason === 'io server disconnect') {
        this.handleReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.isConnected = false;
      this.isConnecting = false;
      this.emit('error', error);
      
      // Handle authentication errors
      if (error.message === 'Authentication error' || error.message.includes('401') || error.message.includes('INVALID_TOKEN')) {
        console.error('Authentication failed - token may be invalid');
        this.emit('auth_error', error);
      } else {
        this.handleReconnect();
      }
    });

    this.socket.on('message', (data) => {
      this.emit('message', data);
    });

    this.socket.on('notification', (data) => {
      this.emit('notification', data);
    });

    this.socket.on('prediction_update', (data) => {
      this.emit('prediction_update', data);
    });
  }

  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.emit('max_reconnect_attempts');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      if (this.socket && !this.socket.connected) {
        this.socket.connect();
      }
    }, delay);
  }

  disconnect(force = false) {
    // Decrement reference counter
    if (this.connectionRefs > 0) {
      this.connectionRefs--;
    }
    
    // Only disconnect if no more references or forced
    if (this.connectionRefs <= 0 || force) {
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
        this.isConnected = false;
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.currentToken = null;
        this.currentUserId = null;
        this.connectionRefs = 0; // Reset counter
      }
    } else {
    }
  }

  send(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
      return true;
    }
    console.warn('Socket not connected, cannot send message');
    return false;
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in socket event listener for ${event}:`, error);
        }
      });
    }
  }

  getConnectionStatus() {
    return {
      connected: this.isConnected,
      connecting: this.isConnecting,
      socketId: this.socket?.id || null,
      reconnectAttempts: this.reconnectAttempts,
      socketUrl: this.socket?.io?.uri || null,
      authToken: this.currentToken ? '***' : null,
      userType: 'SANDBOX',
      sandboxUserId: this.currentUserId || null,
      connectionRefs: this.connectionRefs
    };
  }

  // Method to refresh token in socket connection
  refreshToken(newToken, userId = null) {
    if (this.socket) {
      this.currentToken = newToken;
      this.currentUserId = userId || this.currentUserId;
      
      // If connected, disconnect and reconnect with new token
      if (this.socket.connected) {
        this.socket.disconnect();
        setTimeout(() => {
          this.connect(newToken, this.currentUserId);
        }, 100);
      }
    }
  }
}

// Create singleton instance
const socketManager = new SocketManager();

export default socketManager;
